import type { CSSProperties } from "react";

import type { ReviewWall } from "@/api";
import { excerptWindow } from "@/lib/excerpt";
import { pageFrame, type PageImage } from "@/lib/usePageImage";

import styles from "./Review.module.css";

/**
 * The person's own drawing around one wall (plan 4 §5) — cropped from the
 * page the readings saw, by the SVG's `viewBox`, so nothing is fetched twice
 * and nothing is re-encoded. Never turned (`lib/excerpt.ts`).
 *
 * A thin line in the open colour runs along the wall: on a sketch with
 * fifteen pencil lines it says which one the question is about. Beside the
 * wall, not across it — the figure stays clear.
 *
 * While the page loads a quiet block holds the place; if it never arrives
 * the excerpt is left out and the row stays usable (the pane says why).
 */
export function WallExcerpt({
  wall,
  image,
  page = null,
}: {
  wall: Pick<ReviewWall, "start" | "end" | "label">;
  image: PageImage;
  /** The page size the reading names — the frame the wall's fractions belong to. */
  page?: { pageWidthPx: number; pageHeightPx: number } | null;
}) {
  if (image.status === "failed") return null;
  if (image.status === "loading") return <span className={styles.excerptWait} aria-hidden="true" />;

  const frame = pageFrame(page, image);
  const cut = excerptWindow(wall.start, wall.end, frame);
  const { x, y, width, height } = cut.view;
  return (
    // A cut from the customer's page — a sheet (design 2026-10-24 §5.3).
    <svg
      className={styles.excerpt}
      data-blatt=""
      data-vertical={cut.vertical}
      role="img"
      aria-label={`Ausschnitt aus deiner Zeichnung um ${wall.label}`}
      viewBox={`${x} ${y} ${width} ${height}`}
      width={cut.display.width}
      height={cut.display.height}
      preserveAspectRatio="xMidYMid meet"
      style={{ "--excerpt-ratio": width / height } as CSSProperties}
    >
      <image href={image.src} x={0} y={0} width={frame.width} height={frame.height} preserveAspectRatio="none" />
      <line
        className={styles.excerptWall}
        x1={wall.start[0] * frame.width}
        y1={wall.start[1] * frame.height}
        x2={wall.end[0] * frame.width}
        y2={wall.end[1] * frame.height}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
